import React from 'react'
import { ApolloProvider, ApolloClient, InMemoryCache, createHttpLink, ApolloLink, fromPromise } from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import { authManager, isMemberOperation } from '@auth'
import { isDev } from '@common'
import { logger } from '@utils'

// Create HTTP link
const httpLink = createHttpLink({
    uri: process.env.REACT_APP_GRAPHQL_URI,
})

// --- Refresh dedupe (global for this module) ---
let refreshPromise = null

const getRefreshPromise = () => {
    if (!refreshPromise) {
        refreshPromise = authManager
            .attemptTokenRefresh()
            .catch(err => {
                // Only log here, let the subsequent flow decide whether to call handleAuthError
                logger.error('[Auth] attemptTokenRefresh threw:', err)
                return false
            })
            .finally(() => {
                refreshPromise = null
            })
    }
    return refreshPromise
}

const isAuthGraphQLError = err => err?.extensions?.code === 'UNAUTHENTICATED' || err?.extensions?.statusCode === 401

const getNetworkStatusCode = networkError => networkError?.statusCode ?? networkError?.response?.status ?? null

// Create auth link to add token to headers and check/refresh token for member operations
// Use ApolloLink to properly handle async token refresh before sending requests
const authLink = new ApolloLink((operation, forward) => {
    const operationName = operation.operationName

    if (operationName) {
        logger.debug('[AuthLink] Operation:', operationName, 'IsMemberOperation:', isMemberOperation(operationName))
    }

    return fromPromise(
        (async () => {
            try {
                if (operationName && isMemberOperation(operationName)) {
                    logger.debug('[AuthLink] Member operation detected, checking token validity...')
                    const isTokenValid = await authManager.ensureTokenValid(300000) // 5 minutes
                    logger.debug('[AuthLink] Token validity check result:', isTokenValid)

                    if (!isTokenValid) {
                        logger.warn(`[AuthLink] Token validation failed for member operation: ${operationName}`)
                        // Let the request proceed; if it's really a 401, let errorLink handle it
                    }
                }

                const authHeaders = authManager.getAuthHeader()

                operation.setContext(({ headers = {} }) => ({
                    headers: {
                        ...headers,
                        ...authHeaders,
                    },
                }))
            } catch (error) {
                logger.error('[AuthLink] Error in auth link:', error)
                // Still forward; 401/UNAUTHENTICATED will be handled by errorLink
            }
        })()
    ).flatMap(() => forward(operation))
})

// Create error link to handle GraphQL errors and auto-refresh tokens
const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
    const { hasRetried = false } = operation.getContext() || {}

    // ---- 1) GraphQL errors ----
    if (graphQLErrors && graphQLErrors.length > 0) {
        // Log all GraphQL errors (preserving original logging behavior)
        graphQLErrors.forEach(({ message, locations, path }) => {
            logger.error(
                `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${JSON.stringify(path)}`
            )
        })

        const authError = graphQLErrors.find(isAuthGraphQLError)

        // Only refresh + retry for auth errors
        if (authError && !hasRetried) {
            operation.setContext(prev => ({ ...prev, hasRetried: true }))

            return fromPromise(getRefreshPromise()).flatMap(refreshed => {
                if (refreshed) {
                    return forward(operation) // retry (will go through authLink again to add header)
                }

                // Refresh failed: delegate to existing auth handler (e.g., logout / redirect)
                authManager.handleAuthError({
                    extensions: authError.extensions,
                    message: authError.message,
                })

                // Important: Don't return empty Observable to swallow errors
                // Not returning => let the original GraphQL error propagate (so UI can detect the failure)
                // eslint-disable-next-line array-callback-return
                return
            })
        }

        // Non-auth GraphQL errors: don't call handleAuthError (to avoid false logout)
        return
    }

    // ---- 2) Network error ----
    if (networkError) {
        logger.error(`[Network error]: ${networkError}`)

        const statusCode = getNetworkStatusCode(networkError)
        const is401Error = statusCode === 401

        if (is401Error && !hasRetried) {
            operation.setContext(prev => ({ ...prev, hasRetried: true }))

            return fromPromise(getRefreshPromise()).flatMap(refreshed => {
                if (refreshed) {
                    return forward(operation)
                }

                authManager.handleAuthError(networkError)
                // Same: don't swallow errors, let the original networkError propagate
                // eslint-disable-next-line array-callback-return
                return
            })
        }

        // Non-401 network errors: only log, let upper layer handle (or you can add generic toast here)
        return
    }

    return
})

const client = new ApolloClient({
    link: ApolloLink.from([errorLink, authLink, httpLink]),
    cache: new InMemoryCache({
        addTypename: false,
    }),
    connectToDevTools: isDev,
})

const GraphqlProvider = ({ children }) => {
    return <ApolloProvider client={client}>{children}</ApolloProvider>
}

export { client, GraphqlProvider }
